import React from 'react';
import { Link } from 'react-router-dom';
import TeslaRobot from '../components/TeslaRobot';

const HowItWorksPage: React.FC = () => {
  return (
    <div className="how-it-works-page">
      <div className="how-it-works-header">
        <div className="how-it-works-title-container">
          <h1 className="how-it-works-title">
            How It <span className="text-gradient">Works</span>
          </h1>
          <p className="how-it-works-subtitle">
            Understanding the technology behind Digital Workplace AI employees
          </p>
        </div>
        <div className="how-it-works-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>
      
      {/* Technology Overview Section */}
      <div className="how-it-works-section technology-overview">
        <h2>Technology Overview</h2>
        <div className="technology-grid">
          <div className="technology-card">
            <div className="technology-icon">🧠</div>
            <h3>Advanced AI Models</h3>
            <p>
              Our AI employees are powered by state-of-the-art large language models (LLMs) that have been 
              trained on vast amounts of data. These models understand context, can generate human-like text, 
              and learn from interactions to improve over time.
            </p>
          </div>
          
          <div className="technology-card">
            <div className="technology-icon">🔄</div>
            <h3>Continuous Learning</h3>
            <p>
              Digital employees continuously learn from interactions with users, improving their responses 
              and adapting to your organization's specific needs, terminology, and workflows through a 
              process called reinforcement learning from human feedback (RLHF).
            </p>
          </div>
          
          <div className="technology-card">
            <div className="technology-icon">🔐</div>
            <h3>Secure Processing</h3>
            <p>
              All data is processed with enterprise-grade security. Your conversations and tasks remain 
              private and protected, with optional data retention policies to meet your organization's 
              compliance requirements.
            </p>
          </div>
          
          <div className="technology-card">
            <div className="technology-icon">🔌</div>
            <h3>API Integration</h3>
            <p>
              Our AI employees can connect to your existing systems through our comprehensive API, 
              allowing them to access and update information in your CRM, project management tools, 
              and other business applications.
            </p>
          </div>
        </div>
      </div>
      
      {/* How AI Employees Work Section */}
      <div className="how-it-works-section ai-employees">
        <h2>How AI Employees Work</h2>
        <div className="process-flow">
          <div className="process-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Skill-Based Selection</h3>
              <p>
                Each AI employee is specialized with specific skills and knowledge areas, such as HR, 
                development, content creation, or project management. Select the employee that best 
                matches your task requirements.
              </p>
              <div className="step-image">
                <img src="/selection.svg" alt="Selecting an AI employee" className="placeholder-image" />
              </div>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Task Assignment</h3>
              <p>
                Assign tasks to your AI employee through natural language instructions. The more specific 
                and clear your instructions, the better the results will be. You can provide context, 
                requirements, and examples to guide the work.
              </p>
              <div className="step-image">
                <img src="/assignment.svg" alt="Assigning a task" className="placeholder-image" />
              </div>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Task Processing</h3>
              <p>
                The AI employee processes your request using its specialized knowledge and skills. 
                It can analyze data, generate content, solve problems, and create plans based on 
                your instructions and organizational context.
              </p>
              <div className="step-image">
                <img src="/processing.svg" alt="Processing a task" className="placeholder-image" />
              </div>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Result Delivery</h3>
              <p>
                The AI employee delivers the completed task, which might be a document, analysis, 
                plan, code, or other output. You can review the results, provide feedback, and 
                request revisions if needed.
              </p>
              <div className="step-image">
                <img src="/delivery.svg" alt="Delivering results" className="placeholder-image" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Effective Prompt Writing Section */}
      <div className="how-it-works-section prompt-writing">
        <h2>Effective Prompt Writing</h2>
        <p className="section-intro">
          The key to getting the best results from your AI employees is writing effective prompts. 
          Here are some best practices for crafting clear instructions:
        </p>
        
        <div className="prompt-examples">
          <div className="prompt-example">
            <div className="example-header">
              <div className="example-label">Basic Prompt</div>
              <div className="example-rating">⭐⭐</div>
            </div>
            <div className="example-content">
              <p className="prompt-text">"Write a marketing email."</p>
              <div className="example-analysis">
                <h4>Why it's limited:</h4>
                <p>
                  This prompt lacks specificity about the product, audience, purpose, and tone. 
                  The AI employee has to make many assumptions, leading to generic results.
                </p>
              </div>
            </div>
          </div>
          
          <div className="prompt-example">
            <div className="example-header">
              <div className="example-label">Improved Prompt</div>
              <div className="example-rating">⭐⭐⭐⭐⭐</div>
            </div>
            <div className="example-content">
              <p className="prompt-text">
                "Write a marketing email for our new AI-powered project management tool. The target audience 
                is IT managers at mid-sized companies who are struggling with resource allocation. The email 
                should highlight how our tool saves time and improves team productivity. Use a professional 
                but conversational tone, and include a clear call-to-action for a free trial. Keep it under 
                300 words."
              </p>
              <div className="example-analysis">
                <h4>Why it's effective:</h4>
                <p>
                  This prompt provides clear context (what product), specific audience (IT managers), 
                  key benefits to highlight (time-saving, productivity), desired tone (professional but 
                  conversational), required elements (CTA for free trial), and constraints (length).
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="prompt-tips">
          <h3>Tips for Effective Prompts</h3>
          <div className="tips-grid">
            <div className="tip-card">
              <div className="tip-icon">🎯</div>
              <h4>Be Specific</h4>
              <p>
                Clearly state what you want, including any specific requirements, formats, or constraints.
              </p>
            </div>
            
            <div className="tip-card">
              <div className="tip-icon">📚</div>
              <h4>Provide Context</h4>
              <p>
                Include relevant background information, purpose, and how the output will be used.
              </p>
            </div>
            
            <div className="tip-card">
              <div className="tip-icon">👥</div>
              <h4>Define the Audience</h4>
              <p>
                Specify who the output is for, which helps the AI employee adjust tone and complexity.
              </p>
            </div>
            
            <div className="tip-card">
              <div className="tip-icon">📝</div>
              <h4>Use Examples</h4>
              <p>
                When possible, provide examples of the type of output you're looking for.
              </p>
            </div>
            
            <div className="tip-card">
              <div className="tip-icon">🔄</div>
              <h4>Iterate</h4>
              <p>
                If the first result isn't quite right, refine your prompt based on what worked and what didn't.
              </p>
            </div>
            
            <div className="tip-card">
              <div className="tip-icon">📏</div>
              <h4>Set Parameters</h4>
              <p>
                Specify length, format, tone, and style to get more tailored results.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Multiple AI Employees Section */}
      <div className="how-it-works-section multiple-employees">
        <h2>Working with Multiple AI Employees</h2>
        <p className="section-intro">
          One of the key advantages of Digital Workplace is the ability to use multiple AI employees 
          with different specializations to handle complex workflows:
        </p>
        
        <div className="workflow-example">
          <h3>Example Workflow: Marketing Campaign Launch</h3>
          <div className="workflow-diagram">
            <div className="workflow-step">
              <div className="workflow-employee">
                <TeslaRobot width={60} height={60} />
                <h4>Market Research Analyst</h4>
                <div className="employee-skills">
                  <span className="skill-tag">Data Analysis</span>
                  <span className="skill-tag">Market Research</span>
                </div>
              </div>
              <div className="workflow-task">
                <h5>Task:</h5>
                <p>Analyze target audience data and identify key messaging opportunities</p>
                <div className="workflow-output">
                  <h5>Output:</h5>
                  <p>Audience insights report with demographic and behavioral data</p>
                </div>
              </div>
            </div>
            
            <div className="workflow-connector">
              <div className="connector-arrow">→</div>
            </div>
            
            <div className="workflow-step">
              <div className="workflow-employee">
                <TeslaRobot width={60} height={60} />
                <h4>Content Strategist</h4>
                <div className="employee-skills">
                  <span className="skill-tag">Content Creation</span>
                  <span className="skill-tag">Copywriting</span>
                </div>
              </div>
              <div className="workflow-task">
                <h5>Task:</h5>
                <p>Develop messaging strategy and create campaign content based on audience insights</p>
                <div className="workflow-output">
                  <h5>Output:</h5>
                  <p>Campaign copy, social media posts, and email templates</p>
                </div>
              </div>
            </div>
            
            <div className="workflow-connector">
              <div className="connector-arrow">→</div>
            </div>
            
            <div className="workflow-step">
              <div className="workflow-employee">
                <TeslaRobot width={60} height={60} />
                <h4>Project Manager</h4>
                <div className="employee-skills">
                  <span className="skill-tag">Project Management</span>
                  <span className="skill-tag">Scheduling</span>
                </div>
              </div>
              <div className="workflow-task">
                <h5>Task:</h5>
                <p>Create campaign launch timeline and coordinate deployment across channels</p>
                <div className="workflow-output">
                  <h5>Output:</h5>
                  <p>Detailed campaign calendar with tasks, deadlines, and responsibilities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="collaboration-benefits">
          <h3>Benefits of Multi-Employee Collaboration</h3>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🧩</div>
              <h4>Specialized Expertise</h4>
              <p>
                Each AI employee brings deep knowledge in their specific domain, ensuring high-quality 
                output for each part of a complex project.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">⚡</div>
              <h4>Parallel Processing</h4>
              <p>
                Multiple AI employees can work simultaneously on different aspects of a project, 
                dramatically reducing completion time.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">🔄</div>
              <h4>Seamless Handoffs</h4>
              <p>
                Work can flow naturally between AI employees, with each one picking up where the 
                previous one left off and building on their output.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">🔍</div>
              <h4>Diverse Perspectives</h4>
              <p>
                Different AI employees can approach problems from various angles, leading to more 
                comprehensive and innovative solutions.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Integration Capabilities Section */}
      <div className="how-it-works-section integration">
        <h2>Integration Capabilities</h2>
        <p className="section-intro">
          Digital Workplace AI employees can integrate with your existing tools and systems, 
          enhancing their capabilities and streamlining your workflows:
        </p>
        
        <div className="integration-types">
          <div className="integration-type">
            <div className="integration-icon">📊</div>
            <h3>Data Access</h3>
            <p>
              AI employees can access data from your databases, CRM systems, and other data sources 
              to provide insights, generate reports, and make data-driven recommendations.
            </p>
          </div>
          
          <div className="integration-type">
            <div className="integration-icon">📝</div>
            <h3>Document Management</h3>
            <p>
              Connect to document storage systems like Google Drive, SharePoint, or Dropbox to allow 
              AI employees to read, create, and update documents directly.
            </p>
          </div>
          
          <div className="integration-type">
            <div className="integration-icon">📅</div>
            <h3>Calendar & Scheduling</h3>
            <p>
              AI employees can manage calendars, schedule meetings, and send invitations through 
              integration with calendar systems like Google Calendar or Outlook.
            </p>
          </div>
          
          <div className="integration-type">
            <div className="integration-icon">✅</div>
            <h3>Task Management</h3>
            <p>
              Integrate with project management tools like Asana, Trello, or Jira to allow AI employees 
              to create, assign, and update tasks as part of their workflow.
            </p>
          </div>
        </div>
        
        <div className="api-reference">
          <h3>API Reference</h3>
          <p>
            Our comprehensive API allows developers to create custom integrations between Digital Workplace 
            and your organization's unique systems and workflows.
          </p>
          <Link to="/api" className="api-button">
            View API Documentation
          </Link>
        </div>
      </div>
      
      {/* Getting Started CTA */}
      <div className="how-it-works-cta">
        <h2>Ready to Transform Your Workplace?</h2>
        <p>
          Experience the power of AI employees and see how they can revolutionize your team's productivity.
        </p>
        <div className="cta-buttons">
          <Link to="/demo" className="cta-button primary">
            Try the Demo
          </Link>
          <Link to="/employees" className="cta-button secondary">
            Explore AI Employees
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksPage;
